/* Aurholm — <ah-nav>: hover-driven mega menu + sliding underline indicator.
 *
 * Styles: assets/ah-mega-menu.css
 * Markup: snippets/header-mega-menu.liquid
 *
 * Why this sits beside Dawn rather than inside it: the element only ever SETS
 * `details.open`. Dawn's own <header-menu> (details-disclosure.js) still handles
 * the `toggle` event — sticky-header preventHide and the
 * --header-bottom-position-desktop measurement keep working untouched, and a
 * Dawn upgrade to details-disclosure.js merges cleanly.
 *
 * Degradation, in order of what is switched off:
 *   no JS          -> native <details>, click to open. Fully usable.
 *   touch / <990px -> matchMedia fails, every handler returns early, Dawn's
 *                     drawer menu takes over. Nothing here runs.
 *   keyboard       -> <summary> is still focusable and still toggles natively;
 *                     focus additionally moves the indicator, Escape closes.
 */

(function () {
  'use strict';

  /* Hover UI only where a real pointer exists and Dawn actually shows the
     inline menu (base.css: .header__inline-menu is display:none below 990px). */
  const DESKTOP_QUERY = '(min-width: 990px) and (hover: hover) and (pointer: fine)';

  /* Short enough to feel instant, long enough that dragging the pointer across
     the nav towards the cart doesn't flash three panels on the way. */
  const OPEN_DELAY = 60;
  /* Covers the diagonal from a top-level item down into its own panel. */
  const CLOSE_DELAY = 200;

  class AhNav extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.list = this.querySelector('.ah-nav__list');
      this.indicator = this.querySelector('.ah-nav__indicator');
      if (!this.list) return;

      this.items = Array.from(this.list.children).filter((el) => el.tagName === 'LI');
      this.desktop = window.matchMedia(DESKTOP_QUERY);
      this.openIndex = -1;
      this.openTimer = null;
      this.closeTimer = null;

      this.items.forEach((li, index) => {
        li.addEventListener('mouseenter', () => this.onEnter(li, index));

        const item = li.querySelector('.header__menu-item');
        if (item) item.addEventListener('focus', () => this.moveIndicator(li));
      });

      this.addEventListener('mouseenter', () => window.clearTimeout(this.closeTimer));
      this.addEventListener('mouseleave', () => this.scheduleClose());

      /* Focus leaving the nav entirely (Tab past the last link) closes up, the
         same way Dawn's DetailsDisclosure.onFocusOut does for a single menu. */
      this.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!this.contains(document.activeElement)) this.closeAll();
        });
      });

      /* Hover already opened the panel, so the click a mouse user makes on the
         summary would immediately close it. Swallow it on desktop only —
         keyboard Enter/Space still toggles because that fires no pointer click
         on an open <summary> we care about, and touch never reaches here. */
      this.addEventListener('click', (event) => {
        if (!this.desktop.matches) return;
        const summary = event.target.closest('summary.header__menu-item');
        if (summary && summary.parentElement.open && event.detail > 0) event.preventDefault();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeAll();
      });

      const reflow = this.debounce(() => {
        if (this.openIndex === -1) this.restIndicator();
        else this.moveIndicator(this.items[this.openIndex]);
      }, 100);

      window.addEventListener('resize', reflow);
      this.desktop.addEventListener('change', () => this.closeAll());

      this.restIndicator();
    }

    onEnter(li, index) {
      if (!this.desktop.matches) return;

      window.clearTimeout(this.closeTimer);
      window.clearTimeout(this.openTimer);
      this.moveIndicator(li);

      const details = li.querySelector('details.mega-menu');

      this.openTimer = window.setTimeout(() => {
        /* A top-level item with no children still closes whatever is open —
           otherwise the panel hangs around under an unrelated heading. */
        if (!details) this.closeAll({ keepIndicator: true });
        else this.open(details, index);
      }, OPEN_DELAY);
    }

    open(details, index) {
      if (details.open) return;

      const previous = this.querySelector('details.mega-menu[open]');

      if (previous && previous !== details) {
        this.dataset.switch = index > this.openIndex ? 'right' : 'left';
        this.setOpen(previous, false);
      }

      this.setOpen(details, true);
      this.openIndex = index;
    }

    closeAll(options) {
      window.clearTimeout(this.openTimer);

      this.querySelectorAll('details.mega-menu[open]').forEach((details) => this.setOpen(details, false));

      delete this.dataset.switch;
      this.openIndex = -1;

      if (!options || !options.keepIndicator) this.restIndicator();
    }

    scheduleClose() {
      window.clearTimeout(this.openTimer);
      this.closeTimer = window.setTimeout(() => this.closeAll(), CLOSE_DELAY);
    }

    setOpen(details, open) {
      details.open = open;

      const summary = details.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', String(open));
    }

    /* Measures in .header-wrapper coordinates: whatever offsetParent resolves
       to is by definition the box the indicator's own top/left resolve against,
       so the two can never drift apart (sticky header, scroll, zoom). */
    moveIndicator(li) {
      const indicator = this.indicator;
      if (!indicator || !li) return;

      const item = li.querySelector('.header__menu-item');
      const parent = indicator.offsetParent;
      if (!item || !parent) return;

      /* The label span, not the whole item: Dawn pads .header__menu-item by
         1.2rem and the bar should read as belonging to the word. */
      const label = item.querySelector('span') || item;
      const parentRect = parent.getBoundingClientRect();
      const labelRect = label.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();

      const wasVisible = indicator.classList.contains('ah-nav__indicator--on');
      if (!wasVisible) indicator.classList.add('ah-nav__indicator--instant');

      indicator.style.setProperty('--ah-nav-ind-x', `${Math.round(labelRect.left - parentRect.left)}px`);
      indicator.style.setProperty('--ah-nav-ind-y', `${Math.round(itemRect.bottom - parentRect.top)}px`);
      indicator.style.setProperty('--ah-nav-ind-w', `${Math.round(labelRect.width)}px`);
      indicator.classList.add('ah-nav__indicator--on');

      if (!wasVisible) {
        void indicator.offsetWidth; // commit the jump before transitions resume
        indicator.classList.remove('ah-nav__indicator--instant');
      }
    }

    /* Resting place is the current page's top-level item, so the bar reads as a
       "you are here" marker between hovers. No active item — home, search, a
       page outside the menu — and it simply fades out. */
    restIndicator() {
      const active = this.querySelector('.header__active-menu-item');
      const li = active && active.closest('li');

      if (li) this.moveIndicator(li);
      else if (this.indicator) this.indicator.classList.remove('ah-nav__indicator--on');
    }

    debounce(fn, wait) {
      let timer;
      return (...args) => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }

  if (!customElements.get('ah-nav')) customElements.define('ah-nav', AhNav);
})();
